import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Castle, Search, Shield } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import {
  useGetCocLinkQuery,
  useGetPlayerQuery,
  useLazySearchClansQuery,
} from '@/services/qclansApi';
import { classifyQclansError, qclansErrorKey } from '@/services/qclansErrors';
import { cn } from '@/lib/cn';
import type { CocClan } from '@/types/qclans';
import { CocClanDetail } from '@/components/coc/CocClanDetail';

/**
 * Clan surface. Resolves a clan tag from (in order):
 *   1. the `?tag=` query param (set by the profile "view clan" deep link)
 *   2. the linked account's clan (auto-resolved from `GET /v1/players/:tag`)
 *   3. a name search the user runs here
 *
 * Once a tag is chosen, `<CocClanDetail>` renders the clan card + members + the
 * war / CWL / capital tabs (each handling its own private/off-season state).
 */
export function CocClan() {
  const { t } = useTranslation('coc');
  const haptics = useHaptics();
  const [params, setParams] = useSearchParams();

  const tagFromUrl = params.get('tag');

  // Auto-resolve the linked account's clan when no tag is in the URL.
  const linkQuery = useGetCocLinkQuery(undefined, { skip: !!tagFromUrl });
  const firstLinkedTag = linkQuery.data?.accounts[0]?.playerTag;
  const meQuery = useGetPlayerQuery(firstLinkedTag ?? '', {
    skip: !!tagFromUrl || !firstLinkedTag,
  });
  const linkedClanTag = meQuery.data?.clan?.tag ?? null;

  const activeTag = tagFromUrl ?? linkedClanTag;

  // Name search (lazy — fires only on submit).
  const [search, { data: searchData, isFetching: searching, error: searchError }] =
    useLazySearchClansQuery();
  const [name, setName] = useState('');

  const onSearch = () => {
    const q = name.trim();
    if (!q) return;
    haptics.impact('light');
    void search({ name: q });
  };

  const selectClan = (clan: CocClan) => {
    haptics.selection();
    setParams({ tag: clan.tag });
  };

  // When a tag is resolved, show the detail; else show the search picker.
  if (activeTag) {
    return <CocClanDetail tag={activeTag} fromProfile={!tagFromUrl} />;
  }

  const resolving = !tagFromUrl && (linkQuery.isLoading || meQuery.isLoading);

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
          <Castle aria-hidden className="h-5 w-5" />
        </span>
        <h1 className="text-2xl font-bold text-fg">{t('clan.title')}</h1>
      </header>

      {resolving ? (
        <Loader />
      ) : (
        <>
          {/* Search box */}
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
              placeholder={t('clan.searchPlaceholder')}
              className="flex-1 rounded-xl border border-border bg-bg-secondary px-3 py-2.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
            />
            <button
              type="button"
              onClick={onSearch}
              disabled={searching || !name.trim()}
              className="flex items-center justify-center rounded-xl bg-brand px-4 text-brand-fg transition-opacity active:opacity-80 disabled:opacity-50"
              aria-label={t('clan.search')}
            >
              <Search className="h-5 w-5" />
            </button>
          </div>

          {searching ? (
            <Loader />
          ) : searchError ? (
            <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
              {t(qclansErrorKey(classifyQclansError(searchError)))}
            </p>
          ) : searchData ? (
            searchData.items.length === 0 ? (
              <p className="py-6 text-center text-sm text-fg-muted">{t('clan.noResults')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {searchData.items.map((clan) => (
                  <li key={clan.tag}>
                    <button
                      type="button"
                      onClick={() => selectClan(clan)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left',
                        'transition-opacity active:opacity-80',
                      )}
                    >
                      {clan.badgeUrls?.small ? (
                        <img src={clan.badgeUrls.small} alt="" className="h-9 w-9 shrink-0" />
                      ) : (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-brand/15 text-brand">
                          <Shield className="h-4 w-4" />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-fg">{clan.name}</p>
                        <p className="text-[11px] tabular-nums text-fg-muted">
                          {clan.tag} · {t('clan.level', { level: clan.clanLevel })}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </>
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}
